import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, {timelineItemClasses} from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {useAuthUser} from '_contexts/AuthUserProvider';
import {PgHistoryAgentEvent, streamPgHistoryAgentChat} from '_logics/LFCSourceUtil';
import {Fragment, useEffect, useReducer, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AgentCostBadge from './AgentCostBadge';
import AgentToolLog from './AgentToolLog';
import AiMarkdownViewer from '_components/common/AiMarkdownViewer';

type ToolEvent = Extract<PgHistoryAgentEvent, {type: 'tool'}>;

export type AgentChatTurn = {
  id: string;
  prompt: string;
  answer: string;
  tools: ToolEvent[];
  error: string | null;
  done: boolean;
  requestCostUsd: string | null;
  sessionCostUsd: string | null;
};

export type AgentChatThread = {
  id: string;
  sessionId: string;
  title: string;
  createdAt: string;
  turns: AgentChatTurn[];
  activeTurnId: string | null;
};

export type AgentChatEventState = {
  threads: AgentChatThread[];
  activeThreadId: string | null;
};

type AgentChatReducerEvent =
  | PgHistoryAgentEvent
  | {type: 'newThread'; id: string; sessionId: string; createdAt: string}
  | {type: 'selectThread'; id: string}
  | {type: 'start'; id: string; prompt: string};

const updateActiveThread = (
  state: AgentChatEventState,
  update: (thread: AgentChatThread) => AgentChatThread
): AgentChatEventState => ({
  ...state,
  threads: state.threads.map(thread =>
    thread.id === state.activeThreadId ? update(thread) : thread
  )
});

const updateActiveTurn = (
  state: AgentChatEventState,
  update: (turn: AgentChatTurn) => AgentChatTurn
): AgentChatEventState => ({
  ...state,
  threads: state.threads.map(thread => {
    if (thread.id !== state.activeThreadId) return thread;
    return {
      ...thread,
      turns: thread.turns.map(turn => (turn.id === thread.activeTurnId ? update(turn) : turn))
    };
  })
});

export const reduceAgentChatEvent = (
  state: AgentChatEventState,
  event: AgentChatReducerEvent
): AgentChatEventState => {
  if (event.type === 'newThread') {
    const thread: AgentChatThread = {
      id: event.id,
      sessionId: event.sessionId,
      title: 'New Channel',
      createdAt: event.createdAt,
      turns: [],
      activeTurnId: null
    };
    return {...state, threads: [thread, ...state.threads], activeThreadId: event.id};
  }
  if (event.type === 'selectThread') {
    return {...state, activeThreadId: event.id};
  }
  if (event.type === 'start') {
    const turn: AgentChatTurn = {
      id: event.id,
      prompt: event.prompt,
      answer: '',
      tools: [],
      error: null,
      done: false,
      requestCostUsd: null,
      sessionCostUsd: null
    };
    return updateActiveThread(state, thread => ({
      ...thread,
      title: thread.turns.length === 0 ? event.prompt.slice(0, 100) : thread.title,
      turns: [...thread.turns, turn],
      activeTurnId: event.id
    }));
  }
  if (event.type === 'tool') {
    return updateActiveTurn(state, turn => ({...turn, tools: [...turn.tools, event]}));
  }
  if (event.type === 'delta') {
    return updateActiveTurn(state, turn => ({...turn, answer: turn.answer + event.text}));
  }
  if (event.type === 'usage') {
    return updateActiveTurn(state, turn => ({
      ...turn,
      requestCostUsd: event.estimatedCostUsd,
      sessionCostUsd: event.sessionCostUsd
    }));
  }
  if (event.type === 'error') {
    return updateActiveTurn(state, turn => ({...turn, error: event.message}));
  }
  if (event.type === 'done') {
    return updateActiveThread(
      updateActiveTurn(state, turn => ({...turn, done: true})),
      thread => ({...thread, activeTurnId: null})
    );
  }
  return state;
};

const clampThreadListWidth = (width: number, containerWidth?: number) => {
  const maxWidth = containerWidth ? Math.min(480, Math.max(260, containerWidth * 0.5)) : 480;
  return Math.min(Math.max(width, 180), maxWidth);
};

export const clampComposerHeight = (height: number) => Math.min(Math.max(height, 120), 360);

const createSessionId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const canCreateNewThread = (activeThread?: AgentChatThread) =>
  Boolean(activeThread && activeThread.turns.length > 0);

const STORAGE_KEY_PREFIX = 'lfc-agent-chat-threads';
export const MAX_STORED_THREADS = 20;

/**
 * localStorage のキーを会社・ユーザー単位で組み立てる。
 * どちらかが欠けている場合は null を返し、保存・復元自体を行わせない
 * （共有ブラウザでユーザー間に履歴が漏れないようスコープを分ける）。
 */
export const getAgentChatStorageKey = (companyId?: number, userId?: string): string | null => {
  if (companyId === undefined || companyId === null || Number.isNaN(companyId)) return null;
  if (!userId) return null;
  return `${STORAGE_KEY_PREFIX}:${companyId}:${userId}`;
};

/**
 * 復元したスレッドを検証し、進行中フィールドを settled 状態にリセットする。
 * 妥当なスレッドでなければ null を返す。
 */
const sanitizeStoredThread = (value: unknown): AgentChatThread | null => {
  if (!value || typeof value !== 'object') return null;
  const thread = value as Partial<AgentChatThread>;
  if (typeof thread.id !== 'string') return null;
  if (typeof thread.sessionId !== 'string') return null;
  if (!Array.isArray(thread.turns)) return null;
  return {
    id: thread.id,
    sessionId: thread.sessionId,
    title: typeof thread.title === 'string' ? thread.title : 'New Channel',
    createdAt: typeof thread.createdAt === 'string' ? thread.createdAt : '',
    turns: thread.turns as AgentChatTurn[],
    // リロード直後は進行中の turn は無いので、settled 状態で描画されるよう null に戻す。
    activeTurnId: null
  };
};

/**
 * localStorage からスレッド履歴を読み込む。
 * キー欠落・parse 失敗・非配列はすべて空配列にフォールバックする。
 */
export const loadStoredAgentChatThreads = (storageKey: string): AgentChatThread[] => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(sanitizeStoredThread)
      .filter((thread): thread is AgentChatThread => thread !== null);
  } catch {
    return [];
  }
};

/**
 * スレッド履歴を localStorage に保存する。
 * 会話が始まっているスレッド（turn が1つ以上）のみ・最新 MAX_STORED_THREADS 件までを保存する。
 * threads は newest-first（reducer の newThread が先頭に prepend）。
 * quota 超過時は件数を段階的に減らして再試行し、それでも失敗したら黙って諦める（React に例外を投げない）。
 */
export const persistAgentChatThreads = (storageKey: string, threads: AgentChatThread[]): void => {
  const storable = threads.filter(thread => canCreateNewThread(thread));
  for (const count of [MAX_STORED_THREADS, 10, 5, 2, 1]) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(storable.slice(0, count)));
      return;
    } catch {
      // 次のより小さい件数で再試行する
    }
  }
};

export const getThreadChannelName = (thread?: AgentChatThread) =>
  thread?.turns[0]?.prompt.slice(0, 100) || null;

const toScaledUsd = (value: string, scale: number) => {
  const [integerPart, decimalPart = ''] = value.split('.');
  const scaledDecimal = decimalPart.padEnd(scale, '0').slice(0, scale);
  return BigInt(`${integerPart}${scaledDecimal}`);
};

const fromScaledUsd = (value: bigint, scale: number) => {
  const sign = value < 0n ? '-' : '';
  const absolute = value < 0n ? -value : value;
  const digits = absolute.toString().padStart(scale + 1, '0');
  const integerPart = digits.slice(0, -scale);
  const decimalPart = digits.slice(-scale);
  return `${sign}${integerPart}.${decimalPart}`;
};

export const getThreadTotalCostUsd = (thread?: AgentChatThread) => {
  const costs = thread?.turns
    .map(turn => turn.requestCostUsd)
    .filter((cost): cost is string => Boolean(cost));
  if (!costs?.length) return null;

  const scale = Math.max(...costs.map(cost => cost.split('.')[1]?.length ?? 0), 6);
  const total = costs.reduce((sum, cost) => sum + toScaledUsd(cost, scale), 0n);
  return fromScaledUsd(total, scale);
};

const AgentChatPanel = () => {
  const {i18n, t} = useTranslation();
  const authUser = useAuthUser();
  const storageKey = getAgentChatStorageKey(authUser?.companyId, authUser?.userId);
  const [prompt, setPrompt] = useState('');
  const [eventState, dispatchEvent] = useReducer(
    reduceAgentChatEvent,
    storageKey,
    (key): AgentChatEventState => ({
      // activeThreadId は null のまま＝表示中のチャットは常に新規から始める
      // （デバイス切替で表示をリセットする既存 UX を保ちつつ、最近の項目だけ復元する）。
      threads: key ? loadStoredAgentChatThreads(key) : [],
      activeThreadId: null
    })
  );
  const [loading, setLoading] = useState(false);
  const [threadListWidth, setThreadListWidth] = useState(280);
  const [composerHeight, setComposerHeight] = useState(160);
  const [resizing, setResizing] = useState(false);
  const [composerResizing, setComposerResizing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const scrollEndRef = useRef<HTMLDivElement | null>(null);
  const activeThread = eventState.threads.find(thread => thread.id === eventState.activeThreadId);
  const conversationThreads = eventState.threads.filter(canCreateNewThread);
  const threadTotalCostUsd = getThreadTotalCostUsd(activeThread);
  const activeThreadChannelName = getThreadChannelName(activeThread);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({block: 'end'});
  }, [activeThread?.turns, loading]);

  // スレッド履歴を localStorage へ保存。ストリーミング/ポーリング中（loading）は
  // 書き込みが多発するのでスキップし、完了時のスナップショットだけ永続化する。
  useEffect(() => {
    if (!storageKey || loading) return;
    persistAgentChatThreads(storageKey, eventState.threads);
  }, [storageKey, eventState.threads, loading]);

  useEffect(() => {
    if (!resizing) return undefined;

    const handlePointerMove = (event: PointerEvent) => {
      const layout = layoutRef.current;
      if (!layout) return;
      const rect = layout.getBoundingClientRect();
      setThreadListWidth(clampThreadListWidth(event.clientX - rect.left, rect.width));
    };
    const handlePointerUp = () => {
      setResizing(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [resizing]);

  useEffect(() => {
    if (!composerResizing) return undefined;

    const handlePointerMove = (event: PointerEvent) => {
      const thread = threadRef.current;
      if (!thread) return;
      const rect = thread.getBoundingClientRect();
      setComposerHeight(clampComposerHeight(rect.bottom - event.clientY));
    };
    const handlePointerUp = () => {
      setComposerResizing(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [composerResizing]);

  const handleSubmit = async () => {
    const text = prompt.trim();
    if (!text || loading) return;

    let thread = activeThread;
    if (!thread) {
      thread = {
        id: createSessionId(),
        sessionId: createSessionId(),
        title: 'New Channel',
        createdAt: new Date().toLocaleString(),
        turns: [],
        activeTurnId: null
      };
      dispatchEvent({
        type: 'newThread',
        id: thread.id,
        sessionId: thread.sessionId,
        createdAt: thread.createdAt
      });
    }

    const turnId = createSessionId();
    setPrompt('');
    setLoading(true);
    dispatchEvent({type: 'start', id: turnId, prompt: text});
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamPgHistoryAgentChat(
        text,
        thread.sessionId,
        dispatchEvent,
        controller.signal,
        i18n.language?.startsWith('en') ? 'en' : 'ja'
      );
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      dispatchEvent({type: 'error', message: t('AI調査の実行に失敗しました')});
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  };

  const handleNewThread = () => {
    if (!canCreateNewThread(activeThread)) return;

    abortRef.current?.abort();
    abortRef.current = null;
    setPrompt('');
    setLoading(false);
    dispatchEvent({
      type: 'newThread',
      id: createSessionId(),
      sessionId: createSessionId(),
      createdAt: new Date().toLocaleString()
    });
  };

  const handleSelectThread = (threadId: string) => {
    if (loading) return;
    setPrompt('');
    dispatchEvent({type: 'selectThread', id: threadId});
  };

  const handleStop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
  };

  const showWelcome = !activeThread || activeThread.turns.length === 0;
  const renderPromptInput = (centered: boolean) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: centered ? 2 : 1,
        flexShrink: 0,
        height: centered ? 'auto' : composerHeight,
        width: centered ? 'min(720px, 100%)' : '100%'
      }}
    >
      <TextField
        name="comment"
        label="Message"
        fullWidth
        multiline
        rows={centered ? 3 : undefined}
        minRows={centered ? undefined : 2}
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        placeholder={t('ここ1週間で変更されたファイルを探して変更内容を説明して')}
        onKeyDown={e => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            handleSubmit();
          }
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: centered ? 'action.hover' : 'background.paper',
            height: centered ? 'auto' : '100%',
            alignItems: centered ? 'center' : 'stretch'
          },
          '& textarea': {
            overflow: 'auto'
          }
        }}
      />
      <Box sx={{display: 'flex', gap: 1, justifyContent: centered ? 'flex-end' : 'flex-start'}}>
        <Button
          color="primary"
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
          disabled={loading || prompt.trim().length === 0}
        >
          {t('送信')}
        </Button>
        {loading && (
          <Tooltip title={t('停止')}>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<StopIcon />}
              onClick={handleStop}
            >
              {t('停止')}
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      ref={layoutRef}
      sx={{
        height: '100%',
        display: 'flex',
        overflow: 'hidden',
        userSelect: resizing || composerResizing ? 'none' : 'auto',
        cursor: resizing ? 'col-resize' : composerResizing ? 'row-resize' : 'auto'
      }}
    >
      <Box
        sx={{
          width: threadListWidth,
          minWidth: 180,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          p: 1
        }}
      >
        <List dense sx={{flexShrink: 0}}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleNewThread} sx={{borderRadius: 1, px: 1, py: 0.5}}>
              <AddCircleOutlineIcon fontSize="small" sx={{mr: 1, color: 'text.secondary'}} />
              <ListItemText
                primary={t('新規チャット')}
                primaryTypographyProps={{variant: 'body2', noWrap: true}}
              />
            </ListItemButton>
          </ListItem>
        </List>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{display: 'block', px: 1, pt: 2, pb: 0.75, fontWeight: 700}}
        >
          {t('最近の項目')}
        </Typography>
        <List
          dense
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto'
          }}
        >
          {conversationThreads.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', px: 1}}>
              {t('履歴はありません')}
            </Typography>
          )}
          {conversationThreads.map(thread => (
            <ListItem key={thread.id} disablePadding>
              <ListItemButton
                selected={thread.id === eventState.activeThreadId}
                onClick={() => handleSelectThread(thread.id)}
                disabled={loading}
                sx={{
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'action.selected'
                  }
                }}
              >
                <ListItemText
                  primary={getThreadChannelName(thread)}
                  secondary={thread.createdAt}
                  primaryTypographyProps={{variant: 'body2', noWrap: true}}
                  secondaryTypographyProps={{variant: 'caption', noWrap: true}}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label={t('Thread Listの幅を変更')}
        onPointerDown={event => {
          event.preventDefault();
          setResizing(true);
        }}
        sx={{
          width: 7,
          flexShrink: 0,
          cursor: 'col-resize',
          touchAction: 'none',
          borderLeft: 1,
          borderRight: 1,
          borderColor: resizing ? 'primary.main' : 'divider',
          bgcolor: resizing ? 'action.selected' : 'transparent',
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'primary.main'
          }
        }}
      />

      <Box ref={threadRef} sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column'}}>
        <Box sx={{px: 2, py: 1, flexShrink: 0}}>
          <Stack direction="row" spacing={1} alignItems="center">
            {activeThreadChannelName && (
              <Typography variant="subtitle1" noWrap sx={{minWidth: 0, fontWeight: 600}}>
                {activeThreadChannelName}
              </Typography>
            )}
            {threadTotalCostUsd && (
              <Chip
                size="small"
                variant="outlined"
                label={`${t('セッション累計')} $${threadTotalCostUsd}`}
              />
            )}
          </Stack>
        </Box>

        <Box
          sx={{flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column'}}
        >
          {showWelcome ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 3,
                py: 4
              }}
            >
              <Box sx={{width: 'min(760px, 100%)'}}>
                <Typography
                  variant="h4"
                  color="text.primary"
                  sx={{mb: 3, textAlign: 'center', fontWeight: 400}}
                >
                  {t('AI調査で何を調べますか？')}
                </Typography>
                {renderPromptInput(true)}
              </Box>
            </Box>
          ) : (
            <Timeline
              sx={{
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0
                },
                marginBlockStart: 0,
                marginBlockEnd: 0
              }}
            >
              {(activeThread?.turns ?? []).map(turn => {
                const isActive = turn.id === activeThread?.activeTurnId && loading;
                const agentContent = turn.error || turn.answer;

                return (
                  <Fragment key={turn.id}>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot color="success">
                          <PersonIcon />
                        </TimelineDot>
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Typography variant="h6" color="success" component="span">
                          user
                        </Typography>
                        <Typography
                          variant="body2"
                          color="success"
                          sx={{whiteSpace: 'pre-line', lineHeight: 1.1, wordBreak: 'break-word'}}
                        >
                          {turn.prompt}
                        </Typography>
                      </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot color="secondary">
                          <SupportAgentIcon />
                        </TimelineDot>
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent sx={{minWidth: 0}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                          <Typography variant="h6" color="secondary" component="span">
                            assistant
                          </Typography>
                          {isActive && <CircularProgress size={14} />}
                        </Box>
                        {turn.tools.length > 0 && <AgentToolLog events={turn.tools} />}
                        {agentContent ? (
                          <AiMarkdownViewer content={agentContent} loading={false} fill={false} />
                        ) : isActive ? (
                          <Skeleton variant="rounded" height={60} />
                        ) : null}
                        {turn.requestCostUsd && (
                          <AgentCostBadge
                            requestCostUsd={turn.requestCostUsd}
                            sessionCostUsd={turn.sessionCostUsd}
                            requestLabel={t('今回')}
                            showSessionCost={false}
                          />
                        )}
                      </TimelineContent>
                    </TimelineItem>
                  </Fragment>
                );
              })}
            </Timeline>
          )}
          <Box ref={scrollEndRef} />
        </Box>

        {!showWelcome && (
          <>
            <Box
              role="separator"
              aria-orientation="horizontal"
              aria-label={t('Messageの高さを変更')}
              onPointerDown={event => {
                event.preventDefault();
                setComposerResizing(true);
              }}
              sx={{
                height: 7,
                flexShrink: 0,
                cursor: 'row-resize',
                touchAction: 'none',
                borderTop: 1,
                borderBottom: 1,
                borderColor: composerResizing ? 'primary.main' : 'divider',
                bgcolor: composerResizing ? 'action.selected' : 'transparent',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                }
              }}
            />
            {renderPromptInput(false)}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AgentChatPanel;
